import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CButton,
  CFormInput,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CSpinner,
  CBadge,
} from "@coreui/react";

import Project from "./ProjectPage";
import { apiClient } from "services/Api";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft,
  cilCheckCircle,
  cilCloudDownload,
  cilFile,
  cilFolderOpen,
  cilPencil,
  cilPeople,
  cilPlus,
  cilSave,
  cilX,
} from "@coreui/icons";
import CompanyLogo from "components/CompanyLogo";
import LoadingScreen from "components/globals/LoadingScreen";
import ToastNotification from "components/globals/ToastNotification";
import NewProject from "./modals/NewProject";
import DataTableComponent from "components/DataTableComponent";
import Employees from "./modals/Employees";
import DeleteProject from "./modals/DeleteProject";
import EditProject from "./modals/EditProject";
import ApproveProject from "./modals/ApproveProject";
import ShareButton from "./component/ShareButton";
import ProjectCostsDrilldownChart from "./component/ProjectCostsDrilldownChart";
import PersonWorkloadHeatmapTabs from "./component/PersonWorkloadHeatmapTabs";
import { jwtDecode } from "jwt-decode";

// Only STREAM's own client record tracks no-incentive projects.
const NO_INCENTIVE_CLIENT_ID = "20140";

const ClientPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const projetoId = query.get("projeto");

  const [toast, setToast] = useState(null);

  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState(null);

  const [showEmployees, setShowEmployees] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showApproveProject, setShowApproveProject] = useState(false);
  const [showDeleteProject, setShowDeleteProject] = useState(false);
  const [loadingExtract, setLoadingExtract] = useState(false);

  const [selectedProject, setSelectedProject] = useState();
  const navigate = useNavigate();

  const closeApproveProject = useCallback(() => setShowApproveProject(false), []);

  const selectedProjectIsTheoretical = useMemo(
    () =>
      !!selectedProject &&
      (projects || []).some(
        (p) =>
          String(p.id) === String(selectedProject) && p.state === "THEORETICAL",
      ),
    [projects, selectedProject],
  );

  const [handleBack, setHandleBack] = useState(null);
  const [handleSave, setHandleSave] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extractFiles, setExtractFiles] = useState(null);
  const [FilterButton, setFilterButton] = useState(null);
  const [handleDelete, setHandleDelete] = useState(null);
  const [updatingPrivacy, setUpdatingPrivacy] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);

  const handleSaveClick = useCallback(async () => {
    if (!handleSave || saving) return;
    setSaving(true);
    try {
      await handleSave();
    } finally {
      setSaving(false);
    }
  }, [handleSave, saving]);

  const salaryPrivacyAccess = useMemo(() => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        return { isManager: false, isAreaManager: false, userId: null };
      }
      const decoded = jwtDecode(token);
      return {
        isManager: !!decoded.is_manager,
        isAreaManager: !!decoded.is_area_manager,
        userId: decoded.user_id || decoded.user || decoded.id || null,
      };
    } catch (error) {
      console.error("Token error:", error);
      return { isManager: false, isAreaManager: false, userId: null };
    }
  }, []);

  const canEditSalaryPrivacy =
    salaryPrivacyAccess.isManager || salaryPrivacyAccess.isAreaManager;

  const salaryPrivacyOptions = useMemo(() => {
    return (activeUsers || [])
      .map((user) => {
        const fullName = [user.first_name, user.last_name]
          .filter(Boolean)
          .join(" ");
        return {
          label: fullName || user.email || `Utilizador #${user.id}`,
          value: user.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activeUsers]);

  const privacyStatus = useMemo(() => {
    const isOpen = !!client?.allow_salary_access_for_all_users;
    return {
      label: isOpen ? "Privacidade: Aberta" : "Privacidade: Restrita",
      color: isOpen ? "success" : "warning",
      description: isOpen
        ? "Salários disponíveis para toda a equipa interna."
        : "Apenas gestores e utilizadores selecionados podem aceder.",
    };
  }, [client]);

  const privacyDefaults = useMemo(() => {
    return {
      allowAll: !!client?.allow_salary_access_for_all_users,
      allowedUserIds: client?.salary_access_allowed_users || [],
    };
  }, [client]);

  const canShareFinancials = useMemo(() => {
    if (!client) return false;
    if (client.allow_salary_access_for_all_users) {
      return true;
    }
    if (salaryPrivacyAccess.isManager || salaryPrivacyAccess.isAreaManager) {
      return true;
    }
    const allowedIds = Array.isArray(client.salary_access_allowed_users)
      ? client.salary_access_allowed_users.map((id) => Number(id))
      : [];
    return (
      !!salaryPrivacyAccess.userId &&
      allowedIds.includes(Number(salaryPrivacyAccess.userId))
    );
  }, [client, salaryPrivacyAccess]);

  useEffect(() => {
    apiClient
      .get(`/projects/management/${id}/projects/`)
      .then((response) => {
        const data = response.data;
        setClient(data.client);
        setProjects(data.projects);
      })
      .catch((error) => console.error("Erro ao carregar projeto:", error));
  }, [id]);

  useEffect(() => {
    setSelectedProject(projetoId);
  }, [projetoId]);

  useEffect(() => {
    apiClient
      .get("/users/active/")
      .then((response) => setActiveUsers(response.data || []))
      .catch((error) => console.error("Erro ao carregar utilizadores:", error));
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setFilterButton(null);
      setExtractFiles(null);
      setHandleSave(null);
      setHandleDelete(null);
      setHandleBack(() => () => navigate("/projetos/gestao"));
      return;
    }

    setHandleBack(() => () => navigate(`/projetos/gestao/${id}`));
  }, [selectedProject]);

  const updateSalaryPrivacy = useCallback(
    ({ allowAll, allowedUserIds }) => {
      if (!canEditSalaryPrivacy) {
        return Promise.resolve(false);
      }
      setUpdatingPrivacy(true);
      return apiClient
        .put(`/projects/management/${id}/privacy/`, {
          allow_salary_access_for_all_users: allowAll,
          allowed_user_ids: allowedUserIds,
        })
        .then((response) => {
          const allowSalaryToAll =
            !!response.data.allow_salary_access_for_all_users;
          const allowedIds = response.data.allowed_user_ids || [];
          setClient((prev) =>
            prev
              ? {
                  ...prev,
                  allow_salary_access_for_all_users: allowSalaryToAll,
                  salary_access_allowed_users: allowedIds,
                }
              : prev
          );
          setToast({
            title: "Privacidade salarial",
            type: "success",
            message: allowSalaryToAll
              ? "Salários visíveis para todos os utilizadores internos."
              : allowedIds.length
              ? "Salários visíveis apenas para gestores/gestores de área e os utilizadores selecionados."
              : "Salários restringidos a gestores e gestores de área.",
          });
          return true;
        })
        .catch((error) => {
          console.error(error);
          setToast({
            title: "Privacidade salarial",
            type: "error",
            message: "Não foi possível atualizar a privacidade salarial.",
          });
          return false;
        })
        .finally(() => setUpdatingPrivacy(false));
    },
    [canEditSalaryPrivacy, id]
  );

  if (!client)
    return (
      <LoadingScreen message="A carregar cliente..." containerHeight="30vh" />
    );

  return (
    <>
      {/* Toolbar */}
      <CCard
        className="px-3 py-3 mb-3"
        style={{ position: "sticky", top: 65, zIndex: 1040 }}
      >
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <CButton
              color="secondary"
              className="d-flex align-items-center"
              onClick={() => handleBack()}
            >
              <CIcon icon={cilArrowLeft} className="me-2" size="lg" />
              Voltar
            </CButton>

            {/* Grupo 1: Empresa */}
            <div className="d-flex align-items-center gap-1">
              <CButton
                color="info"
                className="d-flex align-items-center"
                onClick={() => setShowEmployees(true)}
              >
                <CIcon icon={cilPeople} className="me-2" size="lg" />
                Funcionários
              </CButton>

              <CDropdown autoClose>
                <CDropdownToggle
                  color="info"
                  className="d-flex align-items-center"
                >
                  <CIcon icon={cilFile} className="me-2" size="lg" />
                  Projeto
                </CDropdownToggle>
                <CDropdownMenu>
                  <CDropdownItem
                    className="d-flex align-items-center"
                    onClick={() => setShowAddProject(true)}
                  >
                    <CIcon icon={cilPlus} className="me-2" size="lg" />
                    Criar
                  </CDropdownItem>
                  <CDropdownItem
                    className="d-flex align-items-center"
                    disabled={!selectedProject}
                    onClick={() => setShowEditProject(true)}
                  >
                    <CIcon icon={cilPencil} className="me-2" size="lg" />
                    Editar
                  </CDropdownItem>
                  <CDropdownItem
                    className="d-flex align-items-center"
                    disabled={!handleDelete}
                    onClick={() => setShowDeleteProject(true)}
                  >
                    <CIcon icon={cilX} className="me-2" size="lg" />
                    Remover
                  </CDropdownItem>
                </CDropdownMenu>
              </CDropdown>

              <ShareButton
                clientId={id}
                canEditPrivacy={canEditSalaryPrivacy}
                canShareFinancials={canShareFinancials}
                privacyStatus={privacyStatus}
                privacyDefaults={privacyDefaults}
                privacyOptions={salaryPrivacyOptions}
                onSubmitPrivacy={updateSalaryPrivacy}
                updatingPrivacy={updatingPrivacy}
              />
            </div>

            {/* Grupo 2: Projeto */}
            <div className="d-flex align-items-center gap-1">
              {selectedProjectIsTheoretical && (
                <CButton
                  color="success"
                  className="d-flex align-items-center text-white"
                  onClick={() => setShowApproveProject(true)}
                >
                  <CIcon icon={cilCheckCircle} className="me-2" size="lg" />
                  Aprovar Projeto
                </CButton>
              )}

              {selectedProject && (
                <CDropdown autoClose>
                  <CDropdownToggle
                    color="primary"
                    className="d-flex align-items-center"
                    disabled={loadingExtract}
                  >
                    {loadingExtract && (
                      <CSpinner component="span" size="sm" className="me-2" />
                    )}
                    {!loadingExtract && (
                      <CIcon
                        icon={cilCloudDownload}
                        className="me-2"
                        size="lg"
                      />
                    )}
                    Exportar Dados
                  </CDropdownToggle>
                  <CDropdownMenu>
                    {extractFiles &&
                      Object.entries(extractFiles)?.map(([label, fnc]) => (
                        <CDropdownItem
                          key={label}
                          onClick={(e) => fnc(setLoadingExtract)}
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {label}
                        </CDropdownItem>
                      ))}
                    {!extractFiles && (
                      <small className="px-3 text-muted">Não Disponível</small>
                    )}
                  </CDropdownMenu>
                </CDropdown>
              )}

              {}
            </div>
          </div>

          {/* Ações finais */}
          <div className="d-flex flex-wrap align-items-center gap-2 ms-lg-auto">
            {FilterButton && FilterButton}

            {handleSave && (
              <CButton
                color="success"
                className="d-flex align-items-center"
                disabled={saving}
                onClick={handleSaveClick}
              >
                {saving ? (
                  <CSpinner component="span" size="sm" className="me-2" />
                ) : (
                  <CIcon icon={cilSave} className="me-2" size="lg" />
                )}
                {saving ? "A guardar" : "Guardar"}
              </CButton>
            )}
          </div>
        </div>
      </CCard>

      <ToastNotification toast={toast} setToast={setToast} />

      {/* Header */}
      <CCard className="mb-3">
        <CCardBody className="px-5 py-4">
          <CRow className="align-items-center">
            <CCol className="text-center mr-5t" md={6}>
              {client.image ? (
                <CompanyLogo logo={client.image} maxWidth={200} />
              ) : (
                <h3 className="mb-0 fw-bold">{client.name}</h3>
              )}
            </CCol>
            <CCol md={6}>
              <CRow className="mb-2">
                <CCol>
                  <strong>Email:</strong>
                  <CFormInput
                    value={client.email || "N.A"}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <strong>Morada:</strong>
                  <CFormInput
                    value={client.address || "N.A"}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {showAddProject && (
        <NewProject
          clientId={id}
          onClose={() => setShowAddProject(false)}
          setToast={setToast}
        />
      )}

      {showEditProject && (
        <EditProject
          clientId={id}
          projectId={selectedProject}
          onClose={() => setShowEditProject(false)}
          setToast={setToast}
        />
      )}

      {showApproveProject && (
        <ApproveProject
          clientId={id}
          projectId={selectedProject}
          onClose={closeApproveProject}
          setToast={setToast}
        />
      )}

      {showDeleteProject && (
        <DeleteProject
          handleDelete={handleDelete}
          onClose={() => setShowDeleteProject(false)}
          setToast={setToast}
        />
      )}

      {showEmployees && (
        <Employees
          clientId={id}
          managementDailyJourney={client.management_daily_journey ?? 8}
          onManagementDailyJourneySaved={(value) =>
            setClient((prev) =>
              prev ? { ...prev, management_daily_journey: value } : prev,
            )
          }
          onClose={() => setShowEmployees(false)}
          setToast={setToast}
        />
      )}

      {!selectedProject && (
        <CCard className="mb-3">
          <CCardHeader>
            <strong>Projetos do Cliente</strong>
          </CCardHeader>
          <CCardBody>
            <DataTableComponent
              columns={[
                {
                  name: "Nome do Projeto",
                  selector: (d) => (
                    <strong
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/projetos/gestao/${id}?projeto=${d.id}`)
                      }
                    >
                      {d.name}
                    </strong>
                  ),
                  center: true,
                },
                {
                  name: "Estado",
                  selector: (d) => (
                    <div
                      className="d-flex flex-wrap justify-content-center gap-1"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`/projetos/gestao/${id}?projeto=${d.id}`)
                      }
                    >
                      <CBadge
                        color={
                          d.state === "THEORETICAL" ? "warning" : "success"
                        }
                      >
                        {d.state === "THEORETICAL" ? "Teórico" : "Aprovado"}
                      </CBadge>
                      {d.management?.no_incentive && (
                        <CBadge color="secondary">Sem Incentivo</CBadge>
                      )}
                    </div>
                  ),
                  center: true,
                },
                {
                  name: "Data de Início",
                  selector: (d) => d.management.start_date,
                  center: true,
                },
                {
                  name: "Data de Conclusão",
                  selector: (d) => d.management.end_date,
                  center: true,
                },
                {
                  name: "Executado Até",
                  selector: (d) => d.management.executed || "N.A",
                  center: true,
                },
                {
                  name: "Entidade Responsável",
                  selector: (d) => d.management.responsible_entity,
                  center: true,
                },
                {
                  name: "Programa",
                  selector: (d) => d.program,
                  center: true,
                },
              ]}
              data={projects || []}
              // onRowClicked={(row, event) => setSelectedProject(row.id)}
              onRowClicked={(row) =>
                navigate(`/projetos/gestao/${id}?projeto=${row.id}`)
              }
              noData={
                <CCard
                  className="border-0"
                  style={{
                    background: "#f5f6fa",
                    width: "100%",
                    minHeight: "220px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CCardBody className="w-100 d-flex flex-column align-items-center justify-content-center text-center">
                    <CIcon
                      icon={cilFolderOpen}
                      size="xxl"
                      className="mb-3 text-muted"
                    />
                    <div className="h5 text-muted mb-2">
                      Nenhum projeto encontrado
                    </div>
                    <div className="text-muted small">
                      Não existem projetos a apresentar neste momento.
                    </div>
                  </CCardBody>
                </CCard>
              }
            />
          </CCardBody>
        </CCard>
      )}

      {selectedProject && (
        <Project
          clientId={id}
          projectId={selectedProject}
          setToast={setToast}
          setHandleBack={setHandleBack}
          setHandleDelete={setHandleDelete}
          setFilterButton={setFilterButton}
          setHandleSave={setHandleSave}
          setExtractFiles={setExtractFiles}
        />
      )}

      {!selectedProject && (
        <>
          {id === NO_INCENTIVE_CLIENT_ID && (
            <ProjectCostsDrilldownChart
              clientId={id}
              noIncentive={true}
              title="Custos por Projeto — Projetos Sem Incentivo"
            />
          )}
          <ProjectCostsDrilldownChart
            clientId={id}
            noIncentive={false}
            title="Custos por Projeto — Projetos Com Incentivo"
          />
          <PersonWorkloadHeatmapTabs
            clientId={id}
            showNoIncentiveTab={id === NO_INCENTIVE_CLIENT_ID}
          />
        </>
      )}
    </>
  );
};

export default ClientPage;
